/**
 * LooseRelationshipFooter
 *
 * Footer block shown inside ForeignKeyPopover for loose-FK cells. Single-row,
 * compact: "Loose relation" label, optional Filtered chip, Edit, Clear. The
 * "+ Add another" affordance lives inside LooseRelationshipEditorDialog (the
 * editor) — not duplicated here.
 */

import { Filter, Pencil, Unlink } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import { toast } from '../../../../components/ui/toaster';
import { useMetaInfo, useInvalidateMetaInfo } from '../../common/hooks/useMetaInfo';
import { removeLooseRelationship } from '../../common/services/metaInfoApi';
import type { LooseRelationship } from '../../common/services/metaInfoApi';
import { looseFkHasFilter } from '../../common/utils/looseFkLabels';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';

type LooseRelationshipFooterProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly sourceSchema?: string;
  readonly sourceTable?: string;
  readonly sourceColumn?: string;
  readonly onCleared: () => void;
  readonly onEditRequested?: () => void;
  readonly activeFk?: LooseRelationship;
};

export function LooseRelationshipFooter({
  connectionId,
  database,
  sourceSchema,
  sourceTable,
  sourceColumn,
  onCleared,
  onEditRequested,
  activeFk,
}: LooseRelationshipFooterProps) {
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const invalidateMetaInfo = useInvalidateMetaInfo();
  const { track } = useAnalytics();

  // Prefer the active tab's declaration when N>1 declarations exist on
  // the source triple. Fall back to the first match for legacy callers
  // that don't pass `activeFk`.
  const looseRel =
    activeFk ??
    (sourceSchema && sourceTable && sourceColumn
      ? (metaInfo?.looseRelationships ?? []).find(
          (r) =>
            r.fromSchema === sourceSchema &&
            r.fromTable === sourceTable &&
            r.fromColumn === sourceColumn,
        )
      : undefined);

  if (!looseRel) return null;

  const handleClear = async () => {
    const confirmed = window.confirm(
      "Remove loose-FK declaration for this column? This won't delete data — only the relationship.",
    );
    if (!confirmed) return;

    try {
      await removeLooseRelationship(connectionId, database, looseRel.id);
      await invalidateMetaInfo(connectionId, database);
      track('feature:loose_fk_removed', { source: 'cell_popover' });
      onCleared();
    } catch (err) {
      toast.error({
        title: 'Failed to remove loose relationship',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="px-3 py-1.5 border-t border-border flex items-center gap-1.5">
      <span className="text-[11px] italic text-muted-foreground shrink-0">Loose</span>
      {activeFk && looseFkHasFilter(activeFk) && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground shrink-0"
              data-testid="loose-fk-filtered-chip"
              onClick={(e) => e.stopPropagation()}
              title="Source / target filter active"
            >
              <Filter className="h-2.5 w-2.5" />
              Filtered
            </button>
          </PopoverTrigger>
          <PopoverContent className="text-xs w-64 p-3" side="top" align="start">
            {activeFk.sourceFilter && activeFk.sourceFilter.length > 0 && (
              <div className="mb-2">
                <div className="font-medium mb-1">Source filter</div>
                {activeFk.sourceFilter.map((g, i) => (
                  <div key={i} className="font-mono text-[11px]">
                    {g.field}{' '}
                    {g.conditions
                      .map((c) =>
                        c.value !== undefined ? `${c.operator} ${String(c.value)}` : c.operator,
                      )
                      .join(' OR ')}
                  </div>
                ))}
              </div>
            )}
            {activeFk.targetFilter && activeFk.targetFilter.length > 0 && (
              <div>
                <div className="font-medium mb-1">Target filter</div>
                {activeFk.targetFilter.map((g, i) => (
                  <div key={i} className="font-mono text-[11px]">
                    {g.field}{' '}
                    {g.conditions
                      .map((c) =>
                        c.value !== undefined ? `${c.operator} ${String(c.value)}` : c.operator,
                      )
                      .join(' OR ')}
                  </div>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
      <div className="flex-1" />
      {onEditRequested && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={onEditRequested}
          data-testid="fk-popover-edit-loose"
          title="Edit loose relationship"
        >
          <Pencil className="h-3 w-3 mr-1" />
          Edit
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 px-1.5 text-xs text-muted-foreground hover:text-destructive"
        onClick={handleClear}
        data-testid="fk-popover-clear-loose"
        title="Remove this user-declared loose relationship"
      >
        <Unlink className="h-3 w-3 mr-1" />
        Clear
      </Button>
    </div>
  );
}
